import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ApiError,
  createStarsInvoice,
  createTonInvoice,
  fetchMe,
  type MeResponse,
  type PricingSnapshot,
} from '@/lib/api';
import { track } from '@/lib/analytics';
import { Icon } from '@/components/Icon';
import {
  haptic,
  notify,
  openExternalLink,
  openStarsInvoice,
  openTelegramLink,
  type InvoiceStatus,
} from '@/lib/telegram';

interface PaywallScreenProps {
  me: MeResponse;
  // onPaid is fired only when a payment definitively succeeded. The parent
  // refreshes `me` and routes to SuccessScreen.
  onPaid: (method: 'stars' | 'ton') => void;
  // onAlreadyActive is fired when the backend tells us the subscription is
  // already active — the paywall shouldn't have been reachable, so we send
  // the user back to home with a soft toast.
  onAlreadyActive: () => void;
  // onError surfaces transient errors as a toast string.
  onError: (msg: string) => void;
  // onBack pops the paywall back to home; bound to Telegram's BackButton too.
  onBack: () => void;
}

type Method = 'stars' | 'ton';
type Phase = 'idle' | 'starting' | 'awaiting_ton';

// formatStars renders a Stars price as "70 ⭐" using the design's mono
// numerals + star glyph. Returned as JSX (not a string) so we can colour the
// glyph in the brand yellow.
function StarsAmount({ amount, size = 16 }: { amount: number; size?: number }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
      <span>{amount}</span>
      <Icon
        name="star"
        size={size}
        stroke="var(--stars)"
        fill="var(--stars)"
        sw={1}
      />
    </span>
  );
}

function formatUsdCents(cents: number): string {
  const whole = Math.floor(cents / 100);
  const fraction = cents % 100;
  return `$${whole}.${String(fraction).padStart(2, '0')}`;
}

export function PaywallScreen({
  me,
  onPaid,
  onAlreadyActive,
  onError,
  onBack,
}: PaywallScreenProps) {
  const { pricing, history } = me;
  const tonEnabled = pricing.ton.enabled;
  const hadTrial = history.had_trial;

  const [phase, setPhase] = useState<Phase>('idle');
  const [pendingMethod, setPendingMethod] = useState<Method | null>(null);
  const tonPollHandle = useRef<number | null>(null);
  const tonGiveUpHandle = useRef<number | null>(null);

  const clearTonTimers = useCallback(() => {
    if (tonPollHandle.current !== null) {
      window.clearInterval(tonPollHandle.current);
      tonPollHandle.current = null;
    }
    if (tonGiveUpHandle.current !== null) {
      window.clearTimeout(tonGiveUpHandle.current);
      tonGiveUpHandle.current = null;
    }
  }, []);

  // Tear down the TON polling timers if the paywall unmounts before the
  // user finishes paying. Without this the interval would leak across
  // screens and keep hitting /me after the user navigated away.
  useEffect(() => clearTonTimers, [clearTonTimers]);

  // miniapp_paywall_viewed fires once per mount — the denominator for the
  // payment funnel. Subsequent renders (state changes inside the screen)
  // don't re-fire because the effect has an empty deps array.
  useEffect(() => {
    track('miniapp_paywall_viewed', {
      subscription_state: me.subscription.state,
      had_trial: me.history.had_trial,
      had_paid: me.history.had_paid,
      stars_first_time: pricing.stars.is_first_time,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Declaration order matters: handleStars and handleTon both reference
  // handleMintError (and handleTon references startTonPolling) in their
  // useCallback bodies, so those helpers must be declared first to be
  // listed honestly in the dependency arrays. Otherwise react-hooks/
  // exhaustive-deps would force a stale-closure foot-gun where the
  // handlers keep an old handleMintError after either prop changes.
  const handleMintError = useCallback(
    (err: unknown, method: Method) => {
      setPhase('idle');
      setPendingMethod(null);
      notify('error');
      if (err instanceof ApiError) {
        if (err.code === 'subscription_already_active' || err.status === 409) {
          onAlreadyActive();
          return;
        }
        if (err.code === 'user_not_registered' || err.status === 404) {
          onError('нужно зарегистрироваться у бота — открой @shushvpnbot');
          return;
        }
        if (err.code === 'ton_disabled' || err.code === 'stars_disabled') {
          onError(
            method === 'ton'
              ? 'ton сейчас недоступен — выбери ⭐'
              : 'звёзды сейчас недоступны — попробуй ton',
          );
          return;
        }
      }
      onError('не удалось создать счёт. попробуй ещё раз');
    },
    [onAlreadyActive, onError],
  );

  const startTonPolling = useCallback(() => {
    clearTonTimers();
    const POLL_MS = 3000;
    const GIVE_UP_MS = 15 * 60 * 1000; // 15 minutes — matches TON_INVOICE_TTL_SECONDS default
    tonPollHandle.current = window.setInterval(async () => {
      try {
        const fresh = await fetchMe();
        if (fresh.subscription.state === 'active' || fresh.subscription.state === 'trial') {
          clearTonTimers();
          notify('success');
          onPaid('ton');
        }
      } catch {
        // network blip — keep polling
      }
    }, POLL_MS);
    tonGiveUpHandle.current = window.setTimeout(() => {
      clearTonTimers();
      setPhase('idle');
      setPendingMethod(null);
      onError('платёж не подтвердился. если ты уже оплатил, подожди немного и обнови экран');
    }, GIVE_UP_MS);
  }, [clearTonTimers, onPaid, onError]);

  const handleStars = useCallback(async () => {
    if (phase !== 'idle') return;
    setPhase('starting');
    setPendingMethod('stars');
    haptic('medium');
    try {
      const invoice = await createStarsInvoice();
      track('miniapp_invoice_opened', { method: 'stars' });
      let status: InvoiceStatus;
      try {
        status = await openStarsInvoice(invoice.invoice_url);
      } catch {
        // Older Telegram clients or local dev. Fall back to opening the link
        // externally; the bot will still deliver the success message.
        openExternalLink(invoice.invoice_url);
        setPhase('idle');
        setPendingMethod(null);
        return;
      }
      if (status === 'paid') {
        notify('success');
        onPaid('stars');
        return;
      }
      // 'cancelled' | 'failed' | 'pending' — leave the user on the paywall.
      if (status === 'failed') {
        notify('error');
        onError('платёж не прошёл — попробуй ещё раз');
      }
      setPhase('idle');
      setPendingMethod(null);
    } catch (err) {
      handleMintError(err, 'stars');
    }
  }, [phase, onPaid, onError, handleMintError]);

  const handleTon = useCallback(async () => {
    if (phase !== 'idle' || !tonEnabled) return;
    setPhase('starting');
    setPendingMethod('ton');
    haptic('medium');
    try {
      const invoice = await createTonInvoice();
      track('miniapp_invoice_opened', { method: 'ton' });
      // Prefer the mini-app deeplink so Crypto Pay opens as a nested mini
      // app inside Telegram (no in-app-browser hop). Fall back to the bot
      // chat URL if the backend didn't return one — still opens inside
      // Telegram chrome via openTelegramLink. Only drop to the external
      // browser as a last resort, which keeps local dev working.
      const target = invoice.mini_app_pay_link || invoice.pay_link;
      if (target.startsWith('https://t.me/')) {
        openTelegramLink(target);
      } else {
        openExternalLink(target);
      }
      // CryptoBot completes asynchronously via webhook → core publishes
      // subscription update → /me reflects it. Poll until we see an active
      // sub or the invoice expires.
      setPhase('awaiting_ton');
      startTonPolling();
    } catch (err) {
      handleMintError(err, 'ton');
    }
  }, [phase, tonEnabled, handleMintError, startTonPolling]);

  const cancelTonWait = useCallback(() => {
    clearTonTimers();
    setPhase('idle');
    setPendingMethod(null);
  }, [clearTonTimers]);

  return (
    <div
      className="no-scrollbar"
      style={{ padding: '20px 22px 80px', overflowY: 'auto', height: '100%' }}
    >
      <PaywallHero pricing={pricing} hadTrial={hadTrial} />

      <PlanCard pricing={pricing} />

      <div style={{ marginTop: 18, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <MethodButton
          method="stars"
          variant="moss"
          label={
            <>
              оплатить через{' '}
              <span style={{ marginLeft: 4, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                звёзды <Icon name="star" size={14} stroke="#fff" fill="#fff" sw={1} />
              </span>
            </>
          }
          subline={
            <StarsAmount amount={pricing.stars.final_amount} size={12} />
          }
          loading={pendingMethod === 'stars' && phase === 'starting'}
          disabled={phase !== 'idle'}
          onClick={handleStars}
        />

        {tonEnabled && (
          <MethodButton
            method="ton"
            variant="paper"
            label="оплатить в ton"
            subline={
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Icon name="globe" size={12} stroke="var(--ink-2)" sw={1.6} />
                {formatUsdCents(pricing.ton.final_usd_cents)} · cryptobot
              </span>
            }
            loading={pendingMethod === 'ton' && phase !== 'idle'}
            disabled={phase !== 'idle'}
            onClick={handleTon}
          />
        )}
      </div>

      {phase === 'awaiting_ton' && (
        <TonAwaitingCard onCancel={cancelTonWait} />
      )}

      <TrustNote />

      <BackLink onBack={onBack} />
    </div>
  );
}

function PaywallHero({
  pricing,
  hadTrial,
}: {
  pricing: PricingSnapshot;
  hadTrial: boolean;
}) {
  const eyebrow = pricing.stars.is_first_time
    ? hadTrial
      ? `${pricing.duration_days} дней · скидка первой оплаты`
      : `${pricing.duration_days} дней · подарок новому пользователю`
    : `${pricing.duration_days} дней без ограничений`;
  return (
    <div style={{ marginBottom: 20 }}>
      <div className="eyebrow" style={{ marginBottom: 12 }}>{eyebrow}</div>
      <h1
        style={{
          fontFamily: "'Instrument Serif', serif",
          fontSize: 44,
          lineHeight: 1.0,
          letterSpacing: '-0.02em',
          margin: 0,
          color: 'var(--ink)',
        }}
      >
        один ключ.{' '}
        <span style={{ fontStyle: 'italic', color: 'var(--moss-deep)' }}>все устройства.</span>
      </h1>
    </div>
  );
}

function PlanCard({ pricing }: { pricing: PricingSnapshot }) {
  const showStrike = pricing.stars.discount_pct > 0;
  return (
    <div
      style={{
        background: '#fbf8f1',
        border: '1px solid var(--paper-3)',
        borderRadius: 22,
        padding: 20,
        position: 'relative',
      }}
    >
      <div className="eyebrow" style={{ marginBottom: 14 }}>
        тариф · месячный
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 14 }}>
        <span
          style={{
            fontFamily: "'Instrument Serif', serif",
            fontSize: 76,
            lineHeight: 0.9,
            letterSpacing: '-0.04em',
            color: 'var(--ink)',
            fontFeatureSettings: '"lnum" 1',
          }}
        >
          {pricing.stars.final_amount}
        </span>
        <div style={{ paddingBottom: 12 }}>
          <div
            style={{
              fontFamily: "'Instrument Serif', serif",
              fontSize: 22,
              fontStyle: 'italic',
              color: 'var(--ink)',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            звёзд
            <Icon
              name="star"
              size={18}
              stroke="var(--stars)"
              fill="var(--stars)"
              sw={1}
            />
          </div>
          <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 4 }}>
            {showStrike ? (
              <>
                вместо{' '}
                <span style={{ textDecoration: 'line-through' }}>
                  {pricing.stars.base_amount}
                </span>{' '}
                · −{pricing.stars.discount_pct}%
              </>
            ) : (
              <>в месяц · продли когда удобно</>
            )}
          </div>
        </div>
      </div>

      {pricing.ton.enabled && (
        <div
          style={{
            marginTop: 16,
            paddingTop: 14,
            borderTop: '1px dashed var(--paper-3)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            fontSize: 13,
          }}
        >
          <span style={{ color: 'var(--ink-3)' }}>либо в ton</span>
          <span
            style={{
              color: 'var(--ink)',
              fontFamily: 'var(--f-mono)',
              fontSize: 12,
            }}
          >
            {formatUsdCents(pricing.ton.final_usd_cents)}
            {pricing.ton.discount_pct > 0 && (
              <span style={{ marginLeft: 6, color: 'var(--ink-3)' }}>
                · −{pricing.ton.discount_pct}%
              </span>
            )}
          </span>
        </div>
      )}
    </div>
  );
}

interface MethodButtonProps {
  method: Method;
  variant: 'moss' | 'paper';
  label: React.ReactNode;
  subline: React.ReactNode;
  loading: boolean;
  disabled: boolean;
  onClick: () => void;
}

function MethodButton({ variant, label, subline, loading, disabled, onClick }: MethodButtonProps) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={variant === 'moss' ? 'btn btn-moss' : 'btn btn-paper'}
      style={{
        height: 60,
        padding: '0 22px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        fontSize: 14.5,
        fontWeight: 500,
        opacity: disabled && !loading ? 0.55 : 1,
        cursor: disabled ? 'default' : 'pointer',
      }}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        {label}
      </span>
      <span
        style={{
          fontSize: 12,
          opacity: variant === 'moss' ? 0.85 : 0.75,
          fontFamily: 'var(--f-mono)',
        }}
      >
        {loading ? 'минутку…' : subline}
      </span>
    </button>
  );
}

function TonAwaitingCard({ onCancel }: { onCancel: () => void }) {
  return (
    <div
      style={{
        marginTop: 18,
        padding: '14px 16px',
        border: '1px solid var(--paper-3)',
        borderRadius: 16,
        background: '#fbf8f1',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          color: 'var(--ink-2)',
          fontSize: 13,
        }}
      >
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: 999,
            background: 'var(--moss)',
            boxShadow: '0 0 0 4px color-mix(in oklab, var(--moss) 20%, transparent)',
          }}
        />
        ждём подтверждение от cryptobot
      </div>
      <div style={{ fontSize: 11.5, color: 'var(--ink-3)', lineHeight: 1.5 }}>
        как только tx подтвердится в сети — подписка активируется. обычно занимает меньше минуты.
      </div>
      <button
        onClick={onCancel}
        className="btn btn-ghost"
        style={{ height: 36, marginTop: 6, fontSize: 12.5 }}
      >
        отменить ожидание
      </button>
    </div>
  );
}

function TrustNote() {
  return (
    <div
      style={{
        marginTop: 20,
        padding: '14px 16px',
        border: '1px dashed var(--paper-3)',
        borderRadius: 16,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
      }}
    >
      <Icon name="lock" size={18} stroke="var(--ink-2)" sw={1.6} />
      <div style={{ fontSize: 12, color: 'var(--ink-2)', lineHeight: 1.5 }}>
        мы не храним логи и не продаём данные. одна оплата — 30 дней без рекламы и ограничений
      </div>
    </div>
  );
}

function BackLink({ onBack }: { onBack: () => void }) {
  return (
    <button
      onClick={onBack}
      className="btn btn-ghost"
      style={{ width: '100%', height: 44, marginTop: 14, fontSize: 13 }}
    >
      назад
    </button>
  );
}
